"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PowerArchitectureAssertions = exports.PowerArchitecturePmicAssertions = exports.PowerArchitectureProjectAssertions = exports.ProtectionInputProtectionEffective = exports.ChargerBatterySafeguardsConfigured = exports.LdoHeadroomSufficient = exports.StageEcRespected = exports.StageAmrRespected = exports.StageStatusOutputsTerminated = exports.StageEnableInputsTerminated = exports.StageSupportComponentsPresent = exports.StageCurrentLimitConfigured = exports.StageOutputVoltageConfigured = exports.LoadSupplyWithinRatings = exports.RailOrderingDefined = exports.RailSourcesIdentified = exports.PowerArchitectureRequirement = void 0;
const enums_1 = require("../enums");
const POWER_ARCHITECTURE_REQUIREMENT_ID = 'req_cadstrompower00000000000000000000000';
exports.PowerArchitectureRequirement = {
    id: POWER_ARCHITECTURE_REQUIREMENT_ID,
    slug: 'cadstrom.power',
    slugPrefix: 'PWR',
    name: 'Power Architecture',
    description: 'Asserts that the project’s power architecture — every input source, conversion stage, protection device, and rail — is correctly configured, sequenced, and operated within its ratings. Project-wide checks are evaluated once per build; per-stage checks are evaluated once per power-stage device.',
};
exports.RailSourcesIdentified = {
    id: 'asrt_pwrrailsourcesident00000000000000000',
    slug: 'cadstrom.power.rails.sources_identified',
    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
    name: 'Every power rail must have an identified source',
    description: 'Every power-carrying net must trace to the source that drives it — an input source (connector, battery), the output of a conversion stage, or an off-board supply — through any series elements (fuses, ferrites, diodes, 0 Ω links, load switches). A rail whose source cannot be established indicates a broken or unmappable power chain, and every downstream conclusion about that rail is unreliable.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.RailOrderingDefined = {
    id: 'asrt_pwrseqrailorder000000000000000000000',
    slug: 'cadstrom.power.sequencing.rail_ordering_defined',
    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
    name: 'Required rail ordering must be enforced',
    description: 'Where a stage or load requires ordered bring-up (a controller supplied before its enable asserts, a core rail before an I/O rail), the order must be enforced in hardware (power-good cascade, daisy-chained enables) or be achievable by the documented firmware control of the enables. Ordering left to chance produces intermittent start-up failures.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.LoadSupplyWithinRatings = {
    id: 'asrt_pwrloadsupplyrange000000000000000000',
    slug: 'cadstrom.power.loads.supply_within_ratings',
    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
    name: 'Every load’s supply must stay within its rated range',
    description: 'The worst-case voltage of every rail — including regulator reference tolerance and divider tolerance — must stay inside the recommended operating range of every load on the rail. A rail that drifts past a load’s recommended maximum shortens its life and voids its electrical specifications.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.StageOutputVoltageConfigured = {
    id: 'asrt_pwrstageoutputvolt000000000000000000',
    slug: 'cadstrom.power.stage.output_voltage_configured',
    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
    name: 'Every converter must be configured to produce its intended output voltage',
    description: 'The stage’s programmed output — feedback divider, strap pins, or digital configuration — must produce the rail voltage its downstream loads require, including worst-case reference and divider tolerance. A mis-programmed divider ships the wrong voltage to every load on the rail.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.StageCurrentLimitConfigured = {
    id: 'asrt_pwrstagecurrentlimit0000000000000000',
    slug: 'cadstrom.power.stage.current_limit_configured',
    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
    name: 'Every configurable current limit must match the expected load',
    description: 'Where the stage exposes a configurable current limit (ILIM resistor, strap, or register default), the configured limit must cover the rail’s expected load current without exceeding what the upstream source can supply. A limit set too low browns out under peak load; one set too high defeats the protection.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.StageSupportComponentsPresent = {
    id: 'asrt_pwrstagesupportcomp00000000000000000',
    slug: 'cadstrom.power.stage.support_components_present',
    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
    name: 'Datasheet-required support components must be present and correctly valued',
    description: 'Every support component the stage’s datasheet requires — bootstrap capacitor, compensation network, minimum input/output capacitance — must be populated with values in the datasheet-permitted range. A missing bootstrap capacitor or under-specified output capacitance prevents switching or destabilizes the loop.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.StageEnableInputsTerminated = {
    id: 'asrt_pwrstageenableterm000000000000000000',
    slug: 'cadstrom.power.stage.enable_inputs_terminated',
    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
    name: 'Every enable and mode input must have a defined state',
    description: 'Every enable, mode, or control input of the stage must be at a defined logic level whenever its supply is present — via pull resistor, hard strap, or a driver that is never high-impedance. An enable left floating while the controlling MCU is in reset gives the stage an undefined power-up state.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.StageStatusOutputsTerminated = {
    id: 'asrt_pwrstagestatusterm000000000000000000',
    slug: 'cadstrom.power.stage.status_outputs_terminated',
    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
    name: 'Used status outputs must be correctly terminated',
    description: 'Every open-drain status output (power-good, charge status, fault/interrupt) that the design consumes must have a pull-up to a rail valid at the reading logic. An unpulled open-drain output never presents a logic high, so the consumer reads a constant state.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.StageAmrRespected = {
    id: 'asrt_pwrstageamr0000000000000000000000000',
    slug: 'cadstrom.power.stage.amr_respected',
    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
    name: 'No operating point may exceed absolute maximum ratings',
    description: 'No pin of the stage may see a voltage or current beyond its absolute maximum rating under any reachable operating condition, including start-up, fault, and OTG/reverse modes. Exceeding an absolute maximum risks permanent damage, not merely degraded performance.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.StageEcRespected = {
    id: 'asrt_pwrstageec00000000000000000000000000',
    slug: 'cadstrom.power.stage.ec_respected',
    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
    name: 'Power stages must operate within recommended conditions',
    description: 'The stage must operate inside its recommended operating conditions at the configured operating point. Operation outside the recommended range is unspecified even when below absolute maximums — the vendor guarantees no parameter there.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.LdoHeadroomSufficient = {
    id: 'asrt_pwrldoheadroom0000000000000000000000',
    slug: 'cadstrom.power.ldo.headroom_sufficient',
    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
    name: 'Every LDO must have sufficient input-output headroom',
    description: 'A linear regulator’s input-to-output differential must exceed its datasheet dropout voltage at the expected load current. In dropout the regulator stops regulating — the output tracks the input minus the pass-element drop, and supply rejection collapses.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.ChargerBatterySafeguardsConfigured = {
    id: 'asrt_pwrchgrbattsafeguards000000000000000',
    slug: 'cadstrom.power.charger.battery_safeguards_configured',
    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
    name: 'Battery charging must have its safety functions configured',
    description: 'A battery charging stage must have its protective functions configured for the pack it charges: temperature sensing (a real thermistor on the NTC input, not a fixed divider), input and charge current limits appropriate to the source and cell, and correct termination behaviour. Missing safeguards allow charging outside the pack’s safe envelope.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.ProtectionInputProtectionEffective = {
    id: 'asrt_pwrprotinputprot00000000000000000000',
    slug: 'cadstrom.power.protection.input_protection_effective',
    requirementDefinitionId: POWER_ARCHITECTURE_REQUIREMENT_ID,
    name: 'Input protection devices must be effective as populated',
    description: 'Reverse-polarity and isolation devices must be fully effective in the populated topology: a P-FET’s gate bias must fully enhance it at operating voltages, an N-FET’s gate drive must exceed its source, and clamps must cover the applied range. A partially-enhanced pass device dissipates excess power and defeats the protection it implements.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.PowerArchitectureProjectAssertions = [
    exports.RailSourcesIdentified,
    exports.RailOrderingDefined,
    exports.LoadSupplyWithinRatings,
];
exports.PowerArchitecturePmicAssertions = [
    exports.StageOutputVoltageConfigured,
    exports.StageCurrentLimitConfigured,
    exports.StageSupportComponentsPresent,
    exports.StageEnableInputsTerminated,
    exports.StageStatusOutputsTerminated,
    exports.StageAmrRespected,
    exports.StageEcRespected,
    exports.LdoHeadroomSufficient,
    exports.ChargerBatterySafeguardsConfigured,
    exports.ProtectionInputProtectionEffective,
];
exports.PowerArchitectureAssertions = [
    ...exports.PowerArchitectureProjectAssertions,
    ...exports.PowerArchitecturePmicAssertions,
];
//# sourceMappingURL=power-architecture.js.map